import { Button, Card, Form, Spin, Switch, DatePicker, message } from 'antd';
import React, { useState, useEffect } from 'react';
import { getFormFieldByType, sanitizeFieldName } from '../../util/methods';
import SubList from '../../components/SubList';
import AssetSearch from '../../components/AssetSearch';
import moment from 'moment';
import Swal from 'sweetalert2';

function Create(props) {
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [filters, setFilters] = useState(props.filters || {});
  const [isActive, setIsActive] = useState(true);
  // Faulty state is set true only via a fuel entry — the form may only turn it OFF.
  const [isFaulty, setIsFaulty] = useState(false);

  const { match, fetch, form, data, loading, title, resource, searchGlobalAssetData, assetData, assetSearchLoading, onSubmit, onAdvanceInstallationDate, authUser } = props;
  const { getFieldDecorator, validateFields, setFieldsValue, getFieldValue } = form;

  const validateDeactivationDate = (rule, value, callback) => {
    const active = form.getFieldValue('isActive');
    const installationDate = form.getFieldValue('installationDate');

    if (active) {
      callback();
      return;
    }

    if (!value) {
      callback('Deactivation Date is required!');
      return;
    }

    if (installationDate && moment(installationDate).isValid() && moment(value).isValid()) {
      if (moment(value).valueOf() <= moment(installationDate).valueOf()) {
        callback('Deactivation Date must be greater than Installation Date!');
        return;
      }
    }

    callback();
  };

  useEffect(() => {
    // console.log(match && match.params);
    if (match && match.params && match.params.id) {
      fetch(match.params.id);
    }
  }, [fetch]);

  useEffect(() => {
    if (data.asset) {
      setSelectedAsset(data.asset._id);
    }
    if (typeof data.isActive === 'boolean') {
      setIsActive(data.isActive);
    }
    setIsFaulty(!!(data && data.isFaultyMeter));
  }, [data]);

  useEffect(() => {
    if (title === 'EDIT' && typeof data.isActive === 'boolean') {
      setFieldsValue({
        isActive: data.isActive,
      });
      setIsActive(data.isActive);
    }
  }, [title, data.isActive, setFieldsValue]);

  const handleSubmit = e => {
    e.preventDefault();
    validateFields((err, values) => {
      if (err) {
        return console.log(err);
      }
      const payload = { ...values };
      if (selectedAsset) {
        payload.asset = selectedAsset;
        const newInstallationDate = moment(payload.installationDate);
        if (!newInstallationDate.isValid()) {
          message.error('Invalid Installation Date');
          return;
        }
        payload.installationDate = newInstallationDate.toDate();
        payload.installationDateTs = payload.installationDate.valueOf();
        payload.initialReading = payload.initialReading ? Number(payload.initialReading) : 0;

        // The faulty flag can only be cleared from this form (it is set true only via a
        // fuel entry). When a previously-faulty meter is unmarked here, keep the original
        // faulty data and stamp when it was cleared.
        const wasFaulty = !!(data && data.isFaultyMeter);
        if (!payload.isFaultyMeter && wasFaulty) {
          const existingFaultyData = (data && data.faultyMeterData) || {};
          payload.faultyMeterData = {
            datetime: existingFaultyData.datetime,
            refSource: existingFaultyData.refSource,
            refId: existingFaultyData.refId && existingFaultyData.refId._id ? existingFaultyData.refId._id : existingFaultyData.refId,
            markedAt: existingFaultyData.markedAt,
            unmarkedAt: new Date(),
          };
        }

        if (match.params && match.params.id) {
          const oldInstallationDate = data && data.installationDate ? moment(data.installationDate) : null;
          const oldInstallationDateTs = oldInstallationDate && oldInstallationDate.isValid() ? oldInstallationDate.valueOf() : null;
          const newInstallationDateTs = payload.installationDateTs;

          if (oldInstallationDateTs && oldInstallationDateTs !== newInstallationDateTs) {
            if (newInstallationDateTs > oldInstallationDateTs) {
              message.error('Only installation date advance is allowed (new date must be earlier than old date)');
              return;
            }

            // Installation date advance mode: other changes are not allowed in same request.
            const originalIsActive = typeof data.isActive === 'boolean' ? data.isActive : true;
            const incomingIsActive = typeof payload.isActive === 'boolean' ? payload.isActive : originalIsActive;

            const originalDeactivationTs = data && data.deactivationDate && moment(data.deactivationDate).isValid()
              ? moment(data.deactivationDate).valueOf()
              : null;
            const incomingDeactivationTs = payload && payload.deactivationDate && moment(payload.deactivationDate).isValid()
              ? moment(payload.deactivationDate).valueOf()
              : null;

            if (incomingIsActive !== originalIsActive || incomingDeactivationTs !== originalDeactivationTs) {
              message.error('When advancing installation date, no other meter changes are allowed in the same update');
              return;
            }

            if (onAdvanceInstallationDate) {
              onAdvanceInstallationDate({ installationDate: payload.installationDate }, match.params.id);
              return;
            }
          }
        }

        // console.log(payload);
        if (match.params && match.params.id) {
          if (payload.deactivationDate) {
            payload.deactivationDate = moment(payload.deactivationDate).toDate();
            payload.deactivationDateTs = payload.deactivationDate.valueOf();
          }
          onSubmit(payload, match.params.id);
        } else {
          // CREATE A SWEET ALERT SAYING, ALL PREVIOUSLY CREATED METER WITH SAME TYPE WILL BECOME INACTIVE
          Swal.fire({
            title: 'Warning!',
            html: `<div><p>This action will make all previously created same type meters for this asset inactive (if exist), do you want to continue?</p></div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Continue',
            cancelButtonText: 'Cancel'
          }).then(result => {
            if (result.isConfirmed) {
              onSubmit(payload);
            }
          });
        }
      }
    });
  };

  const handleAssetSelect = value => {
    if (title === 'EDIT') {
      return;
    }
    const asset = assetData.find(ast => ast._id === value);
    if (asset && asset.assetModel) {
      props.fetchAssetModel(asset.assetModel);
      setSelectedAsset(asset._id);
    }
  };

  useEffect(() => {
    const existingFilters = { ...props.filters };
    if (props.filters && props.filters.meterType && props.filters.meterType.options) {
      existingFilters.meterType.options = { ...existingFilters.meterType.options, disabledOnEdit: true, hidden: (match && match.params && match.params.id) ? false : true, notVisible: (match && match.params && match.params.id) ? false : true };
    }
    setFilters(existingFilters);
  }, [props.filters]);

  // useEffect(() => {
  //   console.log(filters);
  // }, [filters]);

  useEffect(() => {
    if (props.assetModel && props.assetModel.meters) {
      const meters = props.assetModel.meters;
      // console.log(meters);
      const existingFilters = { ...filters };
      if (meters && meters.length > 0) {
        existingFilters.meterType.data = { count: meters.length, data: meters };
        existingFilters.meterType.options = { ...(existingFilters.meterType.options || {}), hidden: false, notVisible: false };
        setFilters(existingFilters);
      } else {
        existingFilters.meterType.options = { ...(existingFilters.meterType.options || {}), hidden: true, notVisible: true };
        setFilters(existingFilters);
      }
    }
  }, [props.assetModel]);

  const isActiveFieldValue = getFieldValue('isActive');
  const isMeterActive = typeof isActiveFieldValue === 'boolean' ? isActiveFieldValue : isActive;

  return (
    <Spin spinning={loading}>
      <Card className='gx-card-metrics' title={`${title} ${sanitizeFieldName('meterUnit')}`}>
        <Form onSubmit={handleSubmit} layout='vertical'>
          {Object.keys(filters).map(item => {
            const fieldConfig = filters[item] || {};
            const fieldOptions = fieldConfig.options || {};
            const shouldHideField = item === 'installationDateAdvanceHistory'
              // faultyMeterData is internal (set via fuel entries); never show it or any
              // of its nested fields in the meter form.
              || item === 'faultyMeterData'
              || (typeof item === 'string' && item.indexOf('faultyMeterData') === 0)
              || fieldOptions.hidden
              || fieldOptions.notVisible
              || (title === 'CREATE' && fieldOptions.hiddenOnCreate);

            if (shouldHideField) {
              return null;
            }

            if (filters[item].type === 'Array' && filters[item].schema) {
              return <SubList key={item} item={filters[item]} form={form} data={data[item]} title={title} resource={resource} />;
            } else {
              if ((item === 'meterNo' || item === 'meterVoucherNo' || item === 'isActive') && title === 'CREATE') {
                return null;
              }
              if (item === 'asset') {
                return (
                  <Form.Item key={'asset'} label={'Enter Asset'} style={{ width: '100%' }}>
                    {getFieldDecorator('asset', {
                      rules: [{ required: true, message: 'Asset is required!' }],
                      initialValue: selectedAsset ? selectedAsset : (data && data.asset && data.asset._id)
                    })(
                      <AssetSearch
                        styleName='gx-lt-icon-search-bar-lg'
                        placeholder='Search Assets...'
                        onChange={title === 'EDIT' ? () => { } : searchGlobalAssetData.bind(this)}
                        onSelect={value => handleAssetSelect(value)}
                        assetData={assetData && assetData.filter(ast => ast.assetModel)} // only assets with assetModel
                        authUser={authUser}
                        loading={assetSearchLoading}
                        disabled={title === 'EDIT'}
                        defaultAsset={data && data.asset ? data.asset : selectedAsset ? { _id: selectedAsset } : null}
                      />
                    )}
                  </Form.Item>
                );
              }
              if (item === 'installationDate') {
                return (
                  <Form.Item key={item} label={sanitizeFieldName(item)}>
                    {getFieldDecorator(item, {
                      rules: [{ required: true, message: 'Installation Date is required!' }],
                      initialValue: data[item] && moment(data[item]).isValid() ? moment(data[item]) : null
                    })(
                      <DatePicker
                        showTime
                        onChange={() => {
                          form.validateFields(['deactivationDate'], { force: true });
                        }}
                      />
                    )}
                  </Form.Item>
                );
              }
              if (title === 'EDIT' && (item === 'isActive' || item === 'deactivationDate')) {
                if (item === 'isActive') {
                  return (
                    <Form.Item key={item} label={sanitizeFieldName(item) + (isMeterActive ? ' (Active)' : ' (Inactive)')}>
                      {getFieldDecorator(item, {
                        initialValue: typeof data.isActive === 'boolean' ? data.isActive : isActive,
                        valuePropName: 'checked'
                      })(
                        <Switch
                          onChange={value => {
                            setIsActive(!!value);
                            if (value) {
                              form.setFields({
                                deactivationDate: {
                                  value: form.getFieldValue('deactivationDate'),
                                  errors: undefined
                                }
                              });
                            }
                            form.validateFields(['deactivationDate'], { force: true });
                          }}
                        />
                      )}
                    </Form.Item>
                  )
                }
                if (item === 'deactivationDate') {
                  return (
                    <Form.Item key={item} label={sanitizeFieldName(item)} required={!isMeterActive}>
                      {getFieldDecorator(item, {
                        rules: [
                          { required: !isMeterActive, message: 'Deactivation Date is required!' },
                          { validator: validateDeactivationDate }
                        ],
                        initialValue: data[item] && moment(data[item]).isValid() ? moment(data[item]) : null
                      })(
                        <DatePicker
                          disabled={isMeterActive}
                          showTime
                        />
                      )}
                    </Form.Item>
                  )
                }
              } else if (item === 'isFaultyMeter') {
                return (
                  <Form.Item key={item} label={sanitizeFieldName(item)}>
                    {getFieldDecorator(item, {
                      initialValue: typeof data.isFaultyMeter === 'boolean' ? data.isFaultyMeter : false,
                      valuePropName: 'checked'
                    })(
                      <Switch
                        // A meter is marked faulty only through a fuel entry, so the form
                        // may only turn it OFF: disabled whenever it is currently not faulty.
                        disabled={!isFaulty}
                        onChange={value => setIsFaulty(!!value)}
                      />
                    )}
                  </Form.Item>
                );
              } else {
                if (item !== 'deactivationDate' && item !== 'isActive') {
                  return getFormFieldByType(filters, item, getFieldDecorator, data, { resource, title });
                }
              }
            }
          })}

          <Form.Item>
            <Button type='primary' className='gx-mb-0' htmlType='submit' loading={loading}>
              {title === 'EDIT' ? 'UPDATE' : 'CREATE'}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </Spin>
  );
}

const CreateForm = Form.create({
  name: 'global_state',
  onFieldsChange(props, changedFields) {
    props.onChange(changedFields);
  }
})(Create);

export default CreateForm;
